/**
 * Гео-IP посетителя для подсказки «Вы в Москве? → московская версия сайта».
 *
 * Лукап делает nginx (ngx_http_geoip2_module, база /var/lib/geoip/dbip-city-lite.mmdb,
 * см. /etc/nginx/conf.d/geoip2.conf) и передаёт результат заголовками X-Geo-*.
 * Node-процессы слушают только 127.0.0.1, а nginx через proxy_set_header всегда
 * перекрывает одноимённые заголовки клиента — поэтому X-Geo-* здесь доверенные.
 * x-geo-branch вычисляет proxy.js; значение клиента он всегда удаляет.
 *
 * Никаких авто-редиректов по гео (SEO): только подсказка, решение за посетителем.
 * Модуль без next/* — используется в proxy.js, route handler и тестах.
 */
import { getAllBranches, isValidBranchCode } from '../config/branches-config';
import { DEFAULT_BRANCH_CODE } from '../config/site-host-env';

export const GEO_HEADERS = {
    country: 'x-geo-country',
    region: 'x-geo-region',
    regionName: 'x-geo-region-name',
    city: 'x-geo-city',
} as const;

/** Филиал региона посетителя (только явное совпадение) — ставит proxy.js. */
export const GEO_BRANCH_HEADER = 'x-geo-branch';
/** Выбор посетителя (подсказка, RegionModal, ссылки «Регионы» в футере). */
export const REGION_CHOICE_COOKIE = 'region_choice';
export const REGION_CHOICE_MAX_AGE = 180 * 24 * 60 * 60;

/** Отладка без московского IP: ?geo_country=RU&geo_region_name=Moscow (только GEO_DEBUG=1). */
const DEBUG_PARAMS: Record<keyof GeoInfo, string> = {
    country: 'geo_country',
    region: 'geo_region',
    regionName: 'geo_region_name',
    city: 'geo_city',
};

export interface GeoInfo {
    /** ISO 3166-1: RU */
    country: string;
    /** ISO 3166-2: RU-MOW (только GeoLite2; в DB-IP Lite пусто) */
    region: string;
    /** Moscow, Moscow Oblast, St.-Petersburg */
    regionName: string;
    /** Moscow, Moscow (Tsentralnyy administrativnyy okrug) */
    city: string;
}

interface HeaderSource {
    get(name: string): string | null;
}

function clean(value: string | null | undefined): string {
    // Только печатные символы: значения уходят в JSON и сравниваются строкой.
    return String(value || '').replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 120);
}

export function isGeoDebugEnabled(env: Record<string, string | undefined> = process.env): boolean {
    return (env.GEO_DEBUG || '').trim() === '1';
}

/**
 * X-Geo-* запроса. debugParams (query) учитываются только при GEO_DEBUG=1 и
 * только если задан хотя бы один geo_* — тогда они полностью заменяют заголовки.
 */
export function readGeoHeaders(headers: HeaderSource, debugParams?: URLSearchParams | null): GeoInfo {
    if (debugParams && Object.values(DEBUG_PARAMS).some((name) => debugParams.has(name))) {
        return {
            country: clean(debugParams.get(DEBUG_PARAMS.country)).toUpperCase(),
            region: clean(debugParams.get(DEBUG_PARAMS.region)).toUpperCase(),
            regionName: clean(debugParams.get(DEBUG_PARAMS.regionName)),
            city: clean(debugParams.get(DEBUG_PARAMS.city)),
        };
    }
    return {
        country: clean(headers.get(GEO_HEADERS.country)).toUpperCase(),
        region: clean(headers.get(GEO_HEADERS.region)).toUpperCase(),
        regionName: clean(headers.get(GEO_HEADERS.regionName)),
        city: clean(headers.get(GEO_HEADERS.city)),
    };
}

/** "St.-Petersburg" / "Leningradskaya Oblast'" -> "st petersburg" / "leningradskaya oblast" */
export function normalizeGeoName(value: string): string {
    return value
        .toLowerCase()
        .replace(/[’'`ʼ"]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

function cityMatches(city: string, names: string[]): boolean {
    const normalized = normalizeGeoName(city);
    if (!normalized) return false;
    return names.some((name) => {
        const target = normalizeGeoName(name);
        return Boolean(target) && (normalized === target || normalized.startsWith(`${target} `));
    });
}

/**
 * Филиал, к региону которого явно относится посетитель (branches-config → geo),
 * или null. Порядок: ISO-код региона, затем название региона, затем город.
 */
export function geoBranchFor(geo: GeoInfo): string | null {
    const regionName = normalizeGeoName(geo.regionName);
    for (const branch of getAllBranches()) {
        const rules = branch.geo;
        if (!rules) continue;
        if (geo.region && rules.regions.includes(geo.region)) return branch.code;
        if (geo.country && geo.country !== rules.country) continue;
        if (regionName && rules.regionNames.some((name) => normalizeGeoName(name) === regionName)) return branch.code;
        if (!regionName && cityMatches(geo.city, rules.cities)) return branch.code;
    }
    return null;
}

/** Филиал посетителя с запасным значением: всё, что не совпало, — филиал по умолчанию. */
export function geoBranchOrDefault(geo: GeoInfo): string {
    return geoBranchFor(geo) || DEFAULT_BRANCH_CODE;
}

// Поисковые и прочие роботы: подсказку им не показываем и x-geo-branch не ставим.
const CRAWLER_UA = /bot\b|bot\/|crawl|spider|slurp|yandex(?!browser)|googleother|google-inspectiontool|mediapartners|lighthouse|pagespeed|headless|phantomjs|facebookexternalhit|bingpreview|ia_archiver/i;

export function isCrawlerUserAgent(userAgent?: string | null): boolean {
    return Boolean(userAgent) && CRAWLER_UA.test(String(userAgent));
}

/** Код филиала из куки region_choice или null. */
export function parseRegionChoice(value?: string | null): string | null {
    const code = clean(value).toLowerCase();
    return code && isValidBranchCode(code) ? code : null;
}

/** region_choice из строки Cookie (document.cookie или заголовок). */
export function regionChoiceFromCookieHeader(cookieHeader?: string | null): string | null {
    for (const part of String(cookieHeader || '').split(';')) {
        const eq = part.indexOf('=');
        if (eq < 0) continue;
        if (part.slice(0, eq).trim() !== REGION_CHOICE_COOKIE) continue;
        let value = part.slice(eq + 1).trim();
        try { value = decodeURIComponent(value); } catch { /* как есть */ }
        return parseRegionChoice(value);
    }
    return null;
}

/**
 * Значение x-geo-branch для запроса страницы: филиал региона посетителя или null,
 * если посетитель — робот, уже выбрал регион (кука) или регион не распознан.
 */
export function requestGeoBranch({
    headers,
    searchParams,
    debug = false,
}: {
    headers: HeaderSource;
    searchParams?: URLSearchParams | null;
    debug?: boolean;
}): string | null {
    if (isCrawlerUserAgent(headers.get('user-agent'))) return null;
    if (regionChoiceFromCookieHeader(headers.get('cookie'))) return null;
    return geoBranchFor(readGeoHeaders(headers, debug ? searchParams : null));
}
